import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { useMap, Marker } from "react-leaflet";
import {
  LatLng,
  type LeafletMouseEvent,
  type Marker as LeafletMarker,
  type Map as LeafletMap,
} from "leaflet";
import { MiniMap } from "@/features/map/components/MiniMap";
import { toast } from "react-toastify";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import leafletImage from "leaflet-image";
import { canvasToFile } from "@/utils/map";

export interface ModifyMapProps {
  // 初期位置の緯度・経度
  initialPosition: LatLng | null;

  // 地図の中心位置が変更された時のコールバック
  onPositionChange?: (position: LatLng) => void;

  // 地図のズームレベル (例: 1-20)
  zoom?: number;

  // ズームレベルが変更された時のコールバック
  onZoomChange?: (zoom: number) => void;

  // 地図の編集が可能かどうか
  isEditable?: boolean;

  // 地図の表示サイズ
  width?: string | number;
  height?: string | number;

  // スタイルのカスタマイズ用
  className?: string;
  style?: React.CSSProperties;
}

export interface ModifyMapHandle {
  getCenter: () => LatLng | null;
  setCenter: (pos: LatLng) => void;
  getZoom: () => number | null;
  setZoom: (z: number) => void;
  genImage: () => Promise<File | never>;
}

/**
 * Map のイベント監視を切り出した小さなコンポーネント
 * useMap を使って親 MapContainer のインスタンスにアクセスする
 */
const MapController: React.FC<{
  onPositionChange?: (position: LatLng) => void;
  onMapClick?: (position: LatLng) => void;
}> = ({ onPositionChange, onMapClick }) => {
  const map = useMap();

  React.useEffect(() => {
    const handleMove = (): void => {
      const center = map.getCenter();
      onPositionChange?.(center);
    };

    const handleClick = (event: LeafletMouseEvent): void => {
      onMapClick?.(event.latlng);
    };

    map.on("move", handleMove);
    map.on("click", handleClick);

    return () => {
      map.off("move", handleMove);
      map.off("click", handleClick);
    };
  }, [map, onPositionChange, onMapClick]);

  return null;
};

/**
 * 地図を表示・編集するためのコンポーネント
 *
 * @param props - コンポーネントのプロパティ
 * @param ref - コンポーネントの外部から地図を操作するための参照
 *
 * このコンポーネントは以下の機能を提供します：
 * - 指定された位置に地図を表示
 * - マーカーの配置と移動（編集可能な場合）
 * - 地図の中心位置とズームレベルの制御
 * - 地図画像の生成
 *
 * 外部から以下の操作が可能です：
 * - 地図の中心位置の取得・設定
 * - ズームレベルの取得・設定
 * - 地図の画像ファイル生成
 *
 * @example
 * ```tsx
 * const mapRef = useRef<ModifyMapHandle>(null);
 * <ModifyMap
 *   ref={mapRef}
 *   initialPosition={new LatLng(35.681167, 139.767052)}
 *   isEditable={true}
 *   onPositionChange={(pos) => console.log(pos)}
 * />
 * ```
 */
export const ModifyMap = forwardRef<ModifyMapHandle | null, ModifyMapProps>(
  (props, ref) => {
    const {
      zoom,
      isEditable,
      initialPosition = new LatLng(35.681167, 139.767052),
      width,
      height,
      style,
      onPositionChange,
      className,
    } = props;

    const mapRef = useRef<LeafletMap>(null);
    const markerRef = useRef<LeafletMarker>(null);
    const [markerPos, setMarkerPos] = React.useState<LatLng | null>(
      initialPosition
    );
    // 画像生成前のマーカー位置を保存するためのref
    const markerPosBeforeImageGen = useRef<LatLng | null>(null);

    const containerStyle = {
      width,
      height,
      ...style,
    };

    useImperativeHandle(
      ref,
      (): ModifyMapHandle => ({
        getCenter: () => {
          try {
            return mapRef.current ? mapRef.current.getCenter() : null;
          } catch {
            return null;
          }
        },
        setCenter: (pos: LatLng) => {
          if (mapRef.current) {
            mapRef.current.setView(pos, mapRef.current.getZoom());
          }
        },
        getZoom: () => {
          return mapRef.current ? mapRef.current.getZoom() : null;
        },
        setZoom: (z: number) => {
          if (mapRef.current) {
            mapRef.current.setZoom(z);
          }
        },
        genImage: async (): Promise<File | never> => {
          // 画像生成前にマーカーの位置を保存
          markerPosBeforeImageGen.current = markerPos;

          return await new Promise<File>((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            leafletImage(
              mapRef.current,
              async (err: never, canvas: HTMLCanvasElement | null) => {
                // 画像生成後にマーカーの位置を復元
                // leaflet-imageがDOMを操作してマーカーが消える可能性があるため、
                // 保存しておいた位置を復元する
                if (markerPosBeforeImageGen.current) {
                  // requestAnimationFrameを使って次のフレームでマーカーを復元
                  requestAnimationFrame(() => {
                    setMarkerPos(markerPosBeforeImageGen.current);
                  });
                }

                if (err) {
                  console.error("地図の画像変換に失敗しました", err);
                  toast.error("地図の画像変換に失敗しました");
                  reject(err);
                }

                if (canvas) {
                  const file = await canvasToFile(canvas);
                  resolve(file);
                }
              }
            );
          });
        },
      }),
      [markerPos]
    );

    return (
      <div className={className}>
        {initialPosition && (
          <MiniMap
            zoom={zoom}
            mapRef={mapRef}
            containerStyle={containerStyle}
            center={initialPosition}
            enableZoom={true}
          >
            {markerPos && (
              <Marker
                position={markerPos}
                draggable={isEditable}
                ref={markerRef}
              />
            )}
            <MapController
              onPositionChange={onPositionChange}
              onMapClick={
                isEditable
                  ? (position) => {
                      setMarkerPos(position);
                    }
                  : undefined
              }
            />
          </MiniMap>
        )}
      </div>
    );
  }
);

ModifyMap.displayName = "ModifyMap";

export default ModifyMap;
